/**
 * CameraCommandsPanel — Cinematography presets organized into three
 * collapsible categories: Shot Types, Lighting, and Camera Moves.
 *
 * Appears below RichTextBox when motion prompt edit mode is active.
 * Each button inserts the preset tag into the active editor at the
 * caret position.
 *
 * Round 4:
 *  - Preset insertion no longer emits a closing `[End of …]` tag — only
 *    the opening `[Preset]` token is written into the prompt (item 5).
 *  - Consumers can pass a custom `onInsert` handler. The VideoGenerator
 *    Structured mode wires this to either (a) the open block editor's
 *    RichTextBox, or (b) a new video block when no editor is open
 *    (item 4). When `onInsert` is omitted, the panel falls back to the
 *    previous `richTextBoxRef.wrapSelection` behavior so other consumers
 *    keep working.
 */

import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RichTextBoxHandle } from './RichTextBox'

export const SHOT_TYPE_PRESETS: readonly string[] = [
  'Extreme Close-Up', 'Close-Up', 'Medium Close-Up', 'Medium Shot',
  'Medium Wide', 'Wide Shot', 'Extreme Wide', 'Over-The-Shoulder',
  'POV', 'Low Angle', 'High Angle', 'Dutch Angle',
]

export const LIGHTING_PRESETS: readonly string[] = [
  'Rembrandt', 'Butterfly', 'Split', 'Three-Point',
  'Rim Light', 'High Key', 'Low Key', 'Natural Light',
  'Silhouette', 'Golden Hour', 'Neon', 'Chiaroscuro',
]

export const CAMERA_COMMANDS: readonly string[] = [
  'Zoom In', 'Zoom Out', 'Pan Left', 'Pan Right', 'Tilt Up',
  'Tilt Down', 'Push In', 'Pull Out', 'Truck Left', 'Truck Right',
  'Pedestal Up', 'Pedestal Down', 'Shake', 'Tracking', 'Static',
]

/**
 * Map each cinematography preset VALUE to its react-i18next display-label key
 * (under the `canvas` namespace, `cameraCommandsPanel.presets.<slug>`).
 *
 * IMPORTANT: the preset VALUE is a canonical English token, NOT display text.
 * It is inserted verbatim into the motion prompt as `[Close-Up]` — a model
 * instruction that must stay in the fixed English vocabulary — and is also the
 * `data-testid` slug. Only the pill's DISPLAYED label + `title` tooltip are
 * localized, via `localizeCameraPreset`. This mirrors the built-in style-name /
 * style-group pattern (`localizeStyleName` / `localizeStyleGroup`).
 *
 * The slug is the value lowercased with non-alphanumerics collapsed to `-`
 * (e.g. `Over-The-Shoulder` -> `over-the-shoulder`, `POV` -> `pov`). Keep the
 * `presets.<slug>` keys in every UI locale in lockstep (parity gate enforces the
 * locale side); a missing key falls back to the raw English value.
 */
function presetSlug(preset: string): string {
  return preset.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

/**
 * Localize a cinematography preset's DISPLAY label. The canonical `preset`
 * value stays untouched for prompt insertion + testids; this returns the
 * locale-appropriate label for the pill text and tooltip, falling back to the
 * raw value when a key is missing.
 */
export function localizeCameraPreset(
  preset: string,
  t: (key: string, opts?: { ns?: string; defaultValue?: string }) => string,
): string {
  return t(`cameraCommandsPanel.presets.${presetSlug(preset)}`, {
    ns: 'canvas',
    defaultValue: preset,
  })
}

interface PresetCategory {
  id: string
  labelKey: string
  presets: readonly string[]
  columns: number
  testIdPrefix: string
}

const CATEGORIES: PresetCategory[] = [
  { id: 'shot-types', labelKey: 'cameraCommandsPanel.shotTypes', presets: SHOT_TYPE_PRESETS, columns: 4, testIdPrefix: 'shot-type' },
  { id: 'lighting', labelKey: 'cameraCommandsPanel.lighting', presets: LIGHTING_PRESETS, columns: 4, testIdPrefix: 'lighting' },
  { id: 'camera-moves', labelKey: 'cameraCommandsPanel.cameraMoves', presets: CAMERA_COMMANDS, columns: 5, testIdPrefix: 'camera-cmd' },
]

interface CameraCommandsPanelProps {
  richTextBoxRef: React.RefObject<RichTextBoxHandle | null>
  /**
   * Custom insertion handler. When provided, takes over from the default
   * `richTextBoxRef.wrapSelection` path. Used by VideoGenerator Structured
   * mode to either insert into the open block editor or append a new video
   * block (item 4). When omitted, falls back to the default Freeform behavior.
   */
  onInsert?: (preset: string) => void
}

const ChevronIcon = memo(function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-2.5 h-2.5 text-text-muted/60 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
})

export const CameraCommandsPanel = memo(function CameraCommandsPanel({
  richTextBoxRef,
  onInsert,
}: CameraCommandsPanelProps) {
  const { t } = useTranslation('canvas')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleCommandClick = useCallback(
    (command: string) => {
      // Item 4: custom handler wins. Item 5: only insert the opening tag,
      // never a `[End of …]` marker.
      if (onInsert) {
        onInsert(command)
        return
      }
      const handle = richTextBoxRef.current
      if (!handle) return
      // Item 5: insert `[Command]` only, no closing tag.
      handle.insertAtCursor(`[${command}] `)
    },
    [richTextBoxRef, onInsert],
  )

  return (
    <div data-testid="camera-commands-panel" className="space-y-1">
      {/* Top label with info tooltip */}
      <div className="flex items-center gap-1">
        <span className="text-xxs uppercase tracking-wider text-text-muted">
          {t('cameraCommandsPanel.cinematographyPresets')}
        </span>
        <span className="relative group/info">
          <svg
            className="w-3 h-3 text-text-muted/50 cursor-help"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="absolute bottom-full left-0 mb-1 px-2 py-1 rounded bg-black/90 text-xxs text-white/80 whitespace-nowrap opacity-0 group-hover/info:opacity-100 transition-opacity duration-100 pointer-events-none z-50 shadow-lg">
            {t('cameraCommandsPanel.insertTagsTooltip')}
          </span>
        </span>
      </div>

      {CATEGORIES.map((cat) => {
        const isOpen = !!openSections[cat.id]
        return (
          <div key={cat.id} data-testid={`preset-category-${cat.id}`}>
            <button
              type="button"
              className="flex items-center gap-1 w-full text-left py-0.5 group/cat"
              onMouseDown={(e) => {
                e.preventDefault()
                toggleSection(cat.id)
              }}
              data-testid={`preset-toggle-${cat.id}`}
            >
              <ChevronIcon open={isOpen} />
              <span className="text-xxs font-medium text-text-muted group-hover/cat:text-text-secondary transition-colors">
                {t(cat.labelKey)}
              </span>
              <span className="text-xxs text-text-muted/40 ml-0.5">
                {cat.presets.length}
              </span>
            </button>

            {isOpen && (
              <div
                className="grid gap-1 mt-0.5"
                style={{ gridTemplateColumns: `repeat(${cat.columns}, 1fr)` }}
              >
                {cat.presets.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleCommandClick(preset)
                    }}
                    className="px-1.5 py-1 text-xxs rounded-full border border-border-subtle bg-surface-2 text-text-secondary hover:border-primary/40 hover:text-primary hover:bg-primary/10 transition-colors truncate"
                    title={localizeCameraPreset(preset, t)}
                    data-testid={`${cat.testIdPrefix}-${preset.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {localizeCameraPreset(preset, t)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

CameraCommandsPanel.displayName = 'CameraCommandsPanel'
